import { Alert, Button, Card, CardBody } from '@omnitok/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LoadingSpinner } from '@/shared/components';
import { useLocalStorage } from '@/shared/hooks/useLocalStorage';
import { localizationService } from '../services/localization.service';
import { LOCALIZATION_QUERY_KEY, useLocalizationRun } from '../hooks/useLocalizationRun';
import type { LocalizationArtifactKind, LocalizationRun } from '../types/localization.types';
import LocalizationForm, { type LocalizationFormSubmission } from './LocalizationForm';
import LocalizationRunView from './LocalizationRunView';

function errorMessage(error: unknown): string {
  const candidate = error as {
    message?: string;
    response?: { data?: { error?: { message?: string } } };
  };
  return candidate.response?.data?.error?.message ?? candidate.message ?? 'Ocurrió un error inesperado.';
}

export default function LocalizationContainer() {
  const queryClient = useQueryClient();
  const [runId, setRunId, clearRunId] = useLocalStorage<string | null>('active_localization_run', null);
  const runQuery = useLocalizationRun(runId);

  const createMutation = useMutation({
    mutationFn: (submission: LocalizationFormSubmission) => {
      if (submission.mode === 'excel' && submission.file) {
        return localizationService.createFromExcel(submission.file, {
          sourceLocale: submission.sourceLocale,
          targetLocales: submission.targetLocales,
          force: submission.force,
          replaceDefaultIndexado: submission.replaceDefaultIndexado,
          additionalFields: submission.additionalFields,
        });
      }
      return localizationService.create({
        skus: submission.skus ?? [],
        sourceLocale: submission.sourceLocale,
        targetLocales: submission.targetLocales,
        provider: 'openai',
        force: submission.force,
        replaceDefaultIndexado: submission.replaceDefaultIndexado,
        additionalFields: submission.additionalFields,
      });
    },
    onSuccess: (created) => setRunId(created.runId),
  });

  const approveMutation = useMutation({
    mutationFn: () => localizationService.approve(runId!),
    onSuccess: (run) => queryClient.setQueryData<LocalizationRun>(
      [LOCALIZATION_QUERY_KEY, runId],
      run
    ),
  });

  const applyMutation = useMutation({
    mutationFn: () => localizationService.apply(runId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LOCALIZATION_QUERY_KEY, runId] }),
  });

  const selectModelMutation = useMutation({
    mutationFn: ({ sku, modelId }: { sku: string; modelId: string }) =>
      localizationService.selectModel(runId!, sku, modelId),
    onSuccess: (run) => queryClient.setQueryData<LocalizationRun>(
      [LOCALIZATION_QUERY_KEY, runId],
      run
    ),
  });

  const retryPendingMutation = useMutation({
    mutationFn: () => localizationService.retryPending(runId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LOCALIZATION_QUERY_KEY, runId] }),
  });

  const retryPreviewFailedMutation = useMutation({
    mutationFn: () => localizationService.retryPreviewFailed(runId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LOCALIZATION_QUERY_KEY, runId] }),
  });

  const retryFailedMutation = useMutation({
    mutationFn: () => localizationService.retryFailed(runId!),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [LOCALIZATION_QUERY_KEY, runId] }),
  });

  const newRun = () => {
    if (runId) queryClient.removeQueries({ queryKey: [LOCALIZATION_QUERY_KEY, runId] });
    createMutation.reset();
    approveMutation.reset();
    applyMutation.reset();
    selectModelMutation.reset();
    retryPendingMutation.reset();
    retryPreviewFailedMutation.reset();
    retryFailedMutation.reset();
    clearRunId();
  };

  const approve = () => {
    if (window.confirm('¿Confirmas que revisaste los previews y deseas aprobar este dry-run?')) {
      approveMutation.mutate();
    }
  };

  const apply = () => {
    if (window.confirm('Esta acción puede modificar MongoDB. ¿Deseas aplicar las localizaciones aprobadas?')) {
      applyMutation.mutate();
    }
  };

  const retryPending = () => {
    if (window.confirm('Esto volverá a encolar los SKU pendientes y puede aplicar cambios en MongoDB. ¿Deseas continuar?')) {
      retryPendingMutation.mutate();
    }
  };

  const retryFailed = () => {
    if (window.confirm('Esto reencolará únicamente los SKU fallidos usando los artefactos existentes. ¿Deseas continuar?')) {
      retryFailedMutation.mutate();
    }
  };

  const retryPreviewFailed = () => {
    if (window.confirm('Esto volverá a llamar a OpenAI únicamente para las localizaciones fallidas. La corrida deberá revisarse y aprobarse nuevamente. ¿Deseas continuar?')) {
      retryPreviewFailedMutation.mutate();
    }
  };

  const download = (sku: string, targetLocale: string, kind: LocalizationArtifactKind) =>
    localizationService.downloadArtifact(runId!, sku, targetLocale, kind);

  if (!runId) {
    return (
      <Card noPadding>
        <CardBody>
          {createMutation.error && (
            <Alert className="localization-alert-spacing" variant="error">{errorMessage(createMutation.error)}</Alert>
          )}
          <LocalizationForm
            onSubmit={(submission) => createMutation.mutate(submission)}
            onResume={setRunId}
            isSubmitting={createMutation.isPending}
          />
        </CardBody>
      </Card>
    );
  }

  if (runQuery.isLoading) return <LoadingSpinner message="Cargando corrida de localización…" />;

  if (runQuery.error || !runQuery.data) {
    return (
      <Card noPadding>
        <CardBody>
          <div className="localization-error-state">
            <Alert variant="error">{errorMessage(runQuery.error)}</Alert>
            <p>
              No fue posible recuperar la corrida guardada {runId}.
            </p>
            <Button variant="outline" onClick={newRun}>Crear una nueva corrida</Button>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <>
      {(approveMutation.error || applyMutation.error || selectModelMutation.error || retryPendingMutation.error || retryPreviewFailedMutation.error || retryFailedMutation.error) && (
        <Alert className="localization-alert-spacing" variant="error">
          {errorMessage(
            approveMutation.error
            ?? applyMutation.error
            ?? selectModelMutation.error
            ?? retryPendingMutation.error
            ?? retryPreviewFailedMutation.error
            ?? retryFailedMutation.error
          )}
        </Alert>
      )}
      <LocalizationRunView
        run={runQuery.data}
        sseStatus={runQuery.sseStatus}
        isApproving={approveMutation.isPending}
        isApplying={applyMutation.isPending}
        isRetryingPending={retryPendingMutation.isPending}
        isRetryingPreviewFailed={retryPreviewFailedMutation.isPending}
        isRetryingFailed={retryFailedMutation.isPending}
        onApprove={approve}
        onApply={apply}
        onRetryPending={retryPending}
        onRetryPreviewFailed={retryPreviewFailed}
        onRetryFailed={retryFailed}
        onRefresh={() => { void runQuery.refetch(); }}
        onNewRun={newRun}
        onDownload={download}
        isSelectingModel={selectModelMutation.isPending}
        onSelectModel={async (sku, modelId) => {
          await selectModelMutation.mutateAsync({ sku, modelId });
        }}
      />
    </>
  );
}
